import { format, formatDistanceToNow, isToday, isYesterday, parseISO } from 'date-fns';
import { enUS, ru, uz } from 'date-fns/locale';
import i18n from '../i18n/config';

/**
 * Locale-aware date formatting utilities using date-fns
 * Requirements: 5.1, 5.2, 5.3
 */

/**
 * Get the date-fns locale object for the current i18n language
 */
function getDateFnsLocale() {
  const currentLang = i18n.language;
  
  switch (currentLang) {
    case 'ru':
      return ru;
    case 'uz':
      return uz;
    case 'en':
    default:
      return enUS;
  }
}

/**
 * Format a date according to the current locale
 * Requirement 5.1: Format dates according to active locale's conventions
 * 
 * @param date - Date string or Date object
 * @param formatStr - Format string (default: 'MMM d, yyyy')
 * @returns Formatted date string in current locale
 */
export function formatDate(date: string | Date, formatStr: string = 'MMM d, yyyy'): string {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return format(dateObj, formatStr, { locale: getDateFnsLocale() });
}

/**
 * Format a date and time according to the current locale
 * Requirements 5.1, 5.2: Format dates and times according to active locale's conventions
 * 
 * @param date - Date string or Date object
 * @returns Formatted date and time string in current locale
 */
export function formatDateTime(date: string | Date): string {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return format(dateObj, 'MMM d, yyyy h:mm a', { locale: getDateFnsLocale() });
}

/**
 * Format a time according to the current locale
 * Requirement 5.2: Format times according to active locale's conventions
 * 
 * @param date - Date string or Date object
 * @returns Formatted time string in current locale
 */
export function formatTime(date: string | Date): string {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return format(dateObj, 'h:mm a', { locale: getDateFnsLocale() });
}

/**
 * Format relative time with localized strings
 * Requirement 5.3: Use localized relative time strings
 * 
 * @param date - Date string or Date object
 * @returns Localized relative time string (e.g., "2 hours ago")
 */
export function formatRelativeTime(date: string | Date): string {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  const locale = getDateFnsLocale();
  
  if (isToday(dateObj)) {
    // Use i18n for "Today at" text
    const timeStr = format(dateObj, 'h:mm a', { locale });
    return i18n.t('dates.todayAt', { time: timeStr });
  }
  
  if (isYesterday(dateObj)) {
    // Use i18n for "Yesterday at" text
    const timeStr = format(dateObj, 'h:mm a', { locale });
    return i18n.t('dates.yesterdayAt', { time: timeStr });
  }
  
  return formatDistanceToNow(dateObj, { addSuffix: true, locale });
}

/**
 * Format due date with status (overdue, due today, etc.)
 * This function uses i18n for status messages
 * 
 * @param date - Date string or Date object
 * @returns Formatted due date with status
 */
export function formatDueDate(date: string | Date): string {
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  const now = new Date();
  const isPast = dateObj < now;
  const locale = getDateFnsLocale();
  
  if (isToday(dateObj)) {
    return isPast ? i18n.t('dates.overdue') : i18n.t('dates.dueToday');
  }
  
  if (isYesterday(dateObj)) {
    return i18n.t('dates.overdue');
  }
  
  if (isPast) {
    const distance = formatDistanceToNow(dateObj, { locale });
    return i18n.t('dates.overdueBy', { time: distance });
  }
  
  const distance = formatDistanceToNow(dateObj, { addSuffix: true, locale });
  return i18n.t('dates.due', { time: distance });
}
